import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Sparkles, Zap, Target, ExternalLink, 
  Video, Mic, Type, Headphones, Map, Globe, Navigation,
  LucideIcon
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import D3BackgroundAnimation from './D3BackgroundAnimation'

// Icon mapping for each project type (supports both English and Spanish)
const getProjectIcon = (projectName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    // English
    'Video Transcription': Video,
    'Live Transcription': Mic,
    'Text to Speech': Type,
    'Audio to Text': Headphones,
    'Your Map': Map,
    'Country Map': Globe,
    'World Map': Globe,
    'Get Directions': Navigation,
    // Spanish
    'Transcripción de Video': Video,
    'Transcripción en Vivo': Mic,
    'Texto a Voz': Type,
    'Audio a Texto': Headphones,
    'Tu Mapa': Map,
    'Mapa del País': Globe,
    'Mapa Mundial': Globe,
    'Obtener Direcciones': Navigation
  }
  return iconMap[projectName] || Sparkles
}

const Hero: React.FC = () => {
  const { t } = useLanguage()
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0)
  const [animationKey, setAnimationKey] = useState(0) // Separate key for D3 animation timing
  const projects = t('hero.projects') as unknown as Array<{
    name: string
    description: string
    url: string
  }>
  
  const currentProject = projects[currentProjectIndex]
  const ProjectIcon = currentProject ? getProjectIcon(currentProject.name) : Sparkles

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Auto-rotate projects every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProjectIndex((prevIndex) => 
        (prevIndex + 1) % projects.length
      )
    }, 4000)

    return () => clearInterval(interval)
  }, [projects.length])

  // Delay D3 animation until new content appears (after exit animation completes)
  useEffect(() => {
    // Exit animation duration is 0.8s, wait for it to complete before triggering D3 animation
    const timer = setTimeout(() => {
      setAnimationKey(currentProjectIndex)
    }, 800) // Match the exit animation duration

    return () => clearTimeout(timer)
  }, [currentProjectIndex])

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" role="banner" aria-label="Hero section">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      </div>

      {/* D3.js Background Animation */}
      <div className="absolute inset-0 z-0">
        <D3BackgroundAnimation transitionKey={animationKey} />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-20 blur-xl"
        />
        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-40 right-20 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-xl"
        />
        <motion.div
          animate={{
            y: [0, -15, 0],
            rotate: [0, 3, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-40 left-1/4 w-24 h-24 bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full opacity-20 blur-xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="text-center">
          {/* Project Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="relative min-h-[120px] md:min-h-[160px] lg:min-h-[180px] flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProjectIndex}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -50, scale: 0.9 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                  className="w-full flex flex-col items-center justify-center px-4"
                >
                  {/* Project Icon */}
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mb-6"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                      <div className="relative w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-2xl">
                        <ProjectIcon className="w-10 h-10 md:w-12 md:h-12 text-white" />
                      </div>
                    </div>
                  </motion.div>
                  
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight text-center break-words">
                    <span className="gradient-text">
                      {projects[currentProjectIndex]?.name || 'Loading...'}
                    </span>
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed text-center px-4">
                    {projects[currentProjectIndex]?.description || 'Please wait while we load the project details.'}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Project Navigation Dots */}
            <div className="flex justify-center space-x-2 mt-8">
              {projects && projects.length > 0 ? projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentProjectIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentProjectIndex
                      ? 'bg-blue-600 scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              )) : (
                <div className="text-sm text-gray-500">Loading projects...</div>
              )}
            </div>

            {/* Try Project Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8"
            >
                  <motion.a
                    href={projects[currentProjectIndex]?.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <span>Try {projects[currentProjectIndex]?.name || 'Project'}</span>
                    <ExternalLink className="h-5 w-5 ml-2" />
                  </motion.a>
            </motion.div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('about')}
              className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
            >
              <span>{t('hero.aboutUs')}</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold text-lg hover:border-primary-500 hover:text-primary-600 transition-all duration-300"
            >
              {t('nav.demo')}
            </motion.button>
          </motion.div>

          {/* Feature Icons */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('hero.features.aiPowered')}</h3>
              <p className="text-gray-600 text-sm">{t('hero.features.aiPoweredDesc')}</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('hero.features.fastReliable')}</h3>
              <p className="text-gray-600 text-sm">{t('hero.features.fastReliableDesc')}</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('hero.features.precision')}</h3>
              <p className="text-gray-600 text-sm">{t('hero.features.precisionDesc')}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gray-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero
